import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, Star, ArrowRight, Users, Calendar, Gift } from "lucide-react";

const PriceRow = ({ label, price }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
    <span className="text-gray-700 text-sm">{label}</span>
    <span className="font-semibold text-[#1B2A4A] text-sm whitespace-nowrap ml-4">{price} + HST</span>
  </div>
);

const Section = ({ title, children, accent }) => (
  <div className={`rounded-2xl overflow-hidden shadow-sm border ${accent ? "border-[#C9A96E]/30" : "border-gray-100"}`}>
    <div className={`px-6 py-4 ${accent ? "bg-[#1B2A4A]" : "bg-[#f8f7f4]"}`}>
      <h3 className={`text-lg font-serif font-bold ${accent ? "text-white" : "text-[#1B2A4A]"}`}>{title}</h3>
    </div>
    <div className="bg-white px-6 py-2">{children}</div>
  </div>
);

const Memberships = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[450px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1760926629591-40c9227c46cd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)" }}
        >
          <div className="absolute inset-0 bg-[#1B2A4A]/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Willow Ridge</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-3">2026 Golf Memberships</h1>
          <p className="text-white/70 text-lg mt-4 max-w-2xl">
            Join our club and experience world-class golf in a welcoming, scenic environment.
          </p>
        </div>
      </section>

      {/* New Member Highlight */}
      <section className="bg-[#C9A96E] py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center gap-4">
          <Gift size={28} className="text-[#1B2A4A] shrink-0" />
          <p className="text-[#1B2A4A] font-semibold text-base">
            All new members receive the use of the <strong>Driving Range, Club Storage, and Locker free of charge</strong> for their first season!
          </p>
        </div>
      </section>

      {/* Pricing Sections */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Left: Individual + Junior */}
            <div className="lg:col-span-2 space-y-8">

              {/* Individual */}
              <Section title="Individual Memberships">
                <PriceRow label="Single Adult (40 years +)" price="$1,637.16" />
                <PriceRow label="Intermediate A (19 – 25 years)" price="$884.95" />
                <PriceRow label="Intermediate B (26 – 39 years)" price="$1,238.93" />
                <p className="text-xs text-gray-400 py-3 italic">*** Age is as of April 1, 2026 ***</p>
              </Section>

              {/* Multiple */}
              <Section title="Multiple Memberships" accent>
                <PriceRow label="Couples" price="$2,831.85" />
                <PriceRow label="Family (Parents & 2 kids under 16 years)" price="$3,008.84" />
                <PriceRow label="Intermediate A Couples (19 – 25 years)" price="$1,415.92" />
                <PriceRow label="Intermediate B Couples (26 – 39 years)" price="$1,946.90" />
                <PriceRow label="Out of County Membership" price="$1,415.92" />
                <PriceRow label="Out of Country Couples" price="$2,389.38" />
                <p className="text-xs text-gray-400 py-2 italic">Note: Both members must be in the same age range of the membership category.</p>
                <p className="text-xs text-gray-400 pb-3 italic">*** Age is as of April 1, 2026 ***</p>
              </Section>

              {/* Junior */}
              <Section title="Junior Memberships">
                <PriceRow label="Junior A (under 13)" price="$221.23" />
                <PriceRow label="Junior B (13 to 16)" price="$309.72" />
                <PriceRow label="Junior C (17 and 18)" price="$398.22" />
                <p className="text-xs text-gray-400 py-3 italic">*** Age is as of April 1, 2026 ***</p>
              </Section>

              {/* Add-ons */}
              <Section title="Add-ons / Member Services">
                <PriceRow label="Half Cart Lease (1 Seat)" price="$752.21" />
                <PriceRow label="Back 9 Cart Lease (1 Seat)" price="$442.47" />
                <PriceRow label="Couples Cart Lease" price="$1,238.93" />
                <PriceRow label="Driving Range" price="$353.98" />
                <PriceRow label="Club Storage (Member Price)" price="$88.49" />
                <PriceRow label="Locker (Member Price)" price="$66.37" />
              </Section>
            </div>

            {/* Right: Sidebar info */}
            <div className="space-y-6">
              {/* Payment Plan */}
              <div className="bg-[#f8f7f4] rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar size={20} className="text-[#C9A96E]" />
                  <h3 className="font-serif font-bold text-[#1B2A4A]">Payment Plan</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  A <strong>6-month payment plan</strong> is available. Monthly payments can be made from <strong>December to April</strong>, with the last payment due <strong>April 15</strong>.
                </p>
                <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                  A <strong>$25 administration fee</strong> applies to anyone making monthly payments.
                </p>
              </div>

              {/* Perks */}
              <div className="bg-[#1B2A4A] rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Star size={20} className="text-[#C9A96E]" />
                  <h3 className="font-serif font-bold text-white">Member Benefits</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Unlimited rounds of golf",
                    "Priority tee time booking",
                    "Access to leagues & tournaments",
                    "Clubhouse dining discounts",
                    "Pro shop member pricing",
                    "Scenic countryside setting",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-white/80">
                      <Check size={14} className="text-[#C9A96E] mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image */}
              <div className="rounded-2xl overflow-hidden shadow-sm">
                <img
                  src="https://images.pexels.com/photos/5384079/pexels-photo-5384079.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Willow Ridge Golf Course"
                  className="w-full h-52 object-cover"
                />
              </div>

              {/* CTA */}
              <div className="bg-[#C9A96E]/10 border border-[#C9A96E]/30 rounded-2xl p-6 text-center">
                <p className="text-sm text-gray-600 mb-4">Ready to join? Contact us to get started on your membership today.</p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#1B2A4A] text-white text-sm font-semibold rounded-md hover:bg-[#2a3d62] transition-colors"
                >
                  Inquire Now <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Memberships */}
      <section className="py-20 bg-[#f8f7f4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#C9A96E] text-sm font-medium tracking-[0.2em] uppercase">Business Memberships</span>
            <h2 className="text-4xl font-serif font-bold text-[#1B2A4A] mt-3 mb-4">Corporate Memberships</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Give your team access to world-class golf with a corporate membership package designed for businesses of any size.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Corporate #1 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
                Corporate Membership #1
              </div>
              <div className="space-y-3 text-sm text-gray-600 mb-5">
                <p>One primary member. The company may submit a list of <strong className="text-[#1B2A4A]">4 additional employees</strong>, each with the ability to bring one foursome per day at no charge, excluding league times.</p>
                <p>Includes cart privileges, golf, and driving range access.</p>
              </div>
              <div className="border-t border-gray-100 pt-4 space-y-2 text-sm mb-5">
                <div className="flex justify-between">
                  <span className="text-gray-600">Additional play (same day) — Walking</span>
                  <span className="font-semibold text-[#1B2A4A]">$40 + HST</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Additional play (same day) — With Cart</span>
                  <span className="font-semibold text-[#1B2A4A]">$60 + HST</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-400">Annual Membership</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-[#1B2A4A]">$6,000</span>
                  <p className="text-xs text-gray-400">taxes included</p>
                </div>
              </div>
            </div>

            {/* Corporate #2 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="inline-block bg-[#C9A96E]/20 text-[#1B2A4A] text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
                Corporate Membership #2
              </div>
              <div className="space-y-3 text-sm text-gray-600 mb-5">
                <p>One primary member. The company may submit a list of <strong className="text-[#1B2A4A]">up to 19 additional employees</strong> who may play at no charge, limited to one tee time per day, excluding league times.</p>
                <p>Includes cart privileges, golf, and driving range access. Only the primary member may participate in leagues and club events.</p>
              </div>
              <div className="border-t border-gray-100 pt-4 space-y-2 text-sm mb-5">
                <div className="flex justify-between">
                  <span className="text-gray-600">Non-listed guests in tee time — Walking</span>
                  <span className="font-semibold text-[#1B2A4A]">$40 + HST</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Non-listed guests in tee time — Riding</span>
                  <span className="font-semibold text-[#1B2A4A]">$60 + HST</span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="text-xs text-gray-400">Annual Membership</span>
                <div className="text-right">
                  <span className="text-2xl font-bold text-[#1B2A4A]">$6,000</span>
                  <p className="text-xs text-gray-400">taxes included</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 italic mt-6 max-w-2xl mx-auto">
            *** Proof of corporate employment status will be required upon request from the corporate member's organizer ***
          </p>

          <div className="text-center mt-8">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#1B2A4A] text-white font-semibold rounded-md hover:bg-[#2a3d62] transition-colors"
            >
              Inquire About Corporate Membership <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 bg-[#1B2A4A]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
            Start Your Membership Journey
          </h2>
          <p className="text-white/60 mb-8">
            Contact us today to learn more about memberships or to register for the 2026 season.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#C9A96E] text-[#1B2A4A] font-semibold rounded-md hover:bg-[#d4b97e] transition-colors"
            >
              Get in Touch <ArrowRight size={18} />
            </Link>
            <a
              href="tel:5196768772"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-md hover:bg-white/10 transition-colors"
            >
              Call (519) 676-8772
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Memberships;
